import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ConectaComemorando } from "@/components/Conecta";
import { trancarPagina } from "@/lib/lockScroll";

const MESES = [
  "JANEIRO", "FEVEREIRO", "MARÇO", "ABRIL", "MAIO", "JUNHO",
  "JULHO", "AGOSTO", "SETEMBRO", "OUTUBRO", "NOVEMBRO", "DEZEMBRO",
];

// Cores dos confetes — só CSS.
const CONFETE_CORES = ["#F2B84B", "#5CD0FF", "#4DB6F0", "#FFFFFF", "#2459B8", "#0E7A57"];

export default function MetaBatida({ data, onClose }) {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const doneRef = useRef(false);
  const [closing, setClosing] = useState(false);
  const [rota, setRota] = useState(null);
  const primeiroNome = data?.primeiro_nome || "";
  const mes = useMemo(() => {
    const [ano, m] = String(data?.mes_atual || "").split("-");
    const idx = parseInt(m || "0", 10) - 1;
    return { nome: MESES[idx] || "", ano };
  }, [data?.mes_atual]);

  useEffect(() => {
    // Marca no backend que a meta batida foi mostrada e também consome o
    // "resumo do dia" de hoje — assim, no mesmo dia, o resumo não abre depois.
    api.post("/meta-batida/marcar", {}).catch(() => {});
    const liberar = trancarPagina();
    const onKey = (e) => { if (e.key === "Escape") iniciarSaida(null); };
    document.addEventListener("keydown", onKey);
    containerRef.current?.focus?.();
    return () => {
      document.removeEventListener("keydown", onKey);
      liberar();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const concluir = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    if (rota) navigate(rota);
    onClose?.();
  };

  useEffect(() => {
    if (!closing) return;
    const t = setTimeout(concluir, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closing]);

  const iniciarSaida = (link) => {
    setRota(link || null);
    setClosing(true);
  };

  const onVooAnimationEnd = (e) => {
    if (e.animationName === "cn-sai") concluir();
  };

  // 60 confetes com posição/atraso pseudo-aleatórios estáveis por render.
  const confetes = useMemo(() => Array.from({ length: 60 }, (_, i) => ({
    left: (i * 37) % 100,
    delay: ((i * 13) % 45) / 10,
    duration: 3.5 + ((i * 7) % 22) / 10,
    color: CONFETE_CORES[i % CONFETE_CORES.length],
    horizontal: ((i * 19) % 40) - 20,
  })), []);

  const ui = (
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="meta-batida-titulo"
      tabIndex={-1}
      data-testid="meta-batida"
      className="fixed inset-0 z-[1000] flex items-center justify-center overflow-hidden"
      style={{ background: "#003E62", height: "100dvh", touchAction: "none", overscrollBehavior: "contain" }}
    >
      <div className="confete-camada" aria-hidden>
        {confetes.map((c, i) => (
          <span
            key={i}
            style={{
              left: `${c.left}%`,
              background: c.color,
              animationDelay: `${c.delay}s`,
              animationDuration: `${c.duration}s`,
              transform: `translateX(${c.horizontal}px)`,
            }}
          />
        ))}
      </div>

      <div
        className="relative w-[92%] max-w-[440px] bg-white rounded-[26px] shadow-2xl px-6 pb-6 text-center meta-card"
        style={{
          paddingTop: "5rem",
          paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom))",
          // Sem overflow no cartão externo — a área que rola fica no `.meta-conteudo`.
        }}
      >
        {/* Conecta 3D — FORA da área que rola. Estrutura de camadas do pedido. */}
        <div className="cn-slot cn-slot-meta">
          <div
            className={`cn-voo${closing ? " saindo" : ""}`}
            onAnimationEnd={onVooAnimationEnd}
          >
            <div className="cn-gira">
              <div className="cn-balanca">
                <div className="cn-pula">
                  <ConectaComemorando width={200} height={272} className="cn-img" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="meta-conteudo"
          style={{
            maxHeight: "calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 40px)",
            overflowY: "auto",
          }}
        >
          <div className="text-[11px] font-bold tracking-[0.18em] text-slate-500" data-testid="meta-batida-mes">
            META DE {mes.nome}{mes.ano ? `/${mes.ano}` : ""}
          </div>
          <h1 id="meta-batida-titulo" className="text-2xl font-extrabold mt-1 meta-titulo" style={{ color: "#003E62" }}>
            Parabéns, {primeiroNome}!
          </h1>
          <div className="mt-3 text-5xl font-extrabold meta-pct" style={{ color: "#0E7A57" }} data-testid="meta-batida-pct">
            {Math.round(data?.atingimento || 100)}%
          </div>
          <p className="mt-3 text-[15px] text-navy leading-snug meta-texto">
            Você bateu a meta do mês.
            <br />
            Obrigado pelo trabalho: é assim que a MPires cresce.
          </p>

          <div className="mt-5 flex flex-col gap-2">
            <button
              onClick={() => iniciarSaida("/faturamento")}
              data-testid="meta-batida-ver-faturamento"
              className="w-full h-12 rounded-full text-white font-semibold text-[15px] shadow-sm"
              style={{ background: "#003E62" }}
            >
              Ver meu faturamento
            </button>
            <button
              onClick={() => iniciarSaida(null)}
              data-testid="meta-batida-fechar"
              className="w-full h-12 rounded-full text-navy font-semibold text-[15px] hover:bg-slate-100"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") return ui;
  return createPortal(ui, document.body);
}
